import { useState, useEffect } from "react";
import { useToast } from "@/shared/hooks/use-toast";

interface UsePresetFilesOptions {
  initialCover?: string | null;
  initialLut?: string | null;
  maxSizeImage?: number;
  maxSizeLut?: number;
}

const VALID_LUT_EXT = [".cube", ".3dl", ".lut", ".jpg", ".jpeg", ".png"];

export function usePresetFiles({
  initialCover = null,
  initialLut = null,
  maxSizeImage = 300,
  maxSizeLut = 500,
}: UsePresetFilesOptions = {}) {
  const { toast } = useToast();
  const [imagePreview, setImagePreview] = useState<string | null>(initialCover);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState(false);
  const [lutFile, setLutFile] = useState<File | null>(null);
  const [lutFilename, setLutFilename] = useState<string | null>(initialLut);
  const [lutUrl, setLutUrl] = useState<string | null>(null);
  const [lutError, setLutError] = useState(false);

  useEffect(() => {
    if (initialCover) setImagePreview(initialCover);
  }, [initialCover]);
  useEffect(() => {
    if (initialLut) setLutFilename(initialLut);
  }, [initialLut]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxSizeImage * 1024) {
      toast({ title: "Erro ao carregar imagem", description: `Máximo ${maxSizeImage}KB`, variant: "destructive" });
      e.target.value = "";
      return;
    }
    setImagePreview(null);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
      setImageFile(file);
      setImageError(false);
    };
    reader.readAsDataURL(file);
  };

  const handleLutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxSizeLut * 1024) {
      toast({ title: "Erro ao carregar LUT", description: `Máximo ${maxSizeLut}KB`, variant: "destructive" });
      e.target.value = "";
      return;
    }
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!VALID_LUT_EXT.includes(ext)) {
      toast({ title: "Formato inválido", description: "Use .cube, .3dl, .lut, .jpg, .jpeg ou .png", variant: "destructive" });
      e.target.value = "";
      return;
    }
    setLutFile(null);
    setLutFilename(null);
    setLutUrl(null);
    if ([".jpg", ".jpeg", ".png"].includes(ext)) {
      const reader = new FileReader();
      reader.onloadend = () => setLutUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
    setLutFile(file);
    setLutFilename(file.name);
    setLutError(false);
  };

  return {
    imagePreview,
    imageFile,
    imageError,
    setImagePreview,
    setImageFile,
    setImageError,
    handleImageChange,
    lutFile,
    lutFilename,
    lutUrl,
    lutError,
    setLutFile,
    setLutFilename,
    setLutUrl,
    setLutError,
    handleLutChange,
  };
}
